import React from 'react';
import styled from 'styled-components';
import { useTable, usePagination } from 'react-table';
import Button from '../Buttons/Index';

const Styles = styled.div`
  table {
    border-spacing: 0;
    border: 1px solid #ccc;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th {
      border-bottom: 1px solid #ccc;
      border-right: 1px solid #ccc;
      padding-left: 0.5rem;
    }

    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid #ccc;
      border-right: 1px solid #ccc;

      :last-child {
        border-right: 0;
      }
    }
  }

  .pagination {
    padding-top: 0.5rem;
  }
`;

function Table({
  columns,
  data,
  loading,
  pageCount,
  total,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data,
        manualPagination: true,
        pageCount,
        initialState: { pageIndex, pageSize },
      },
      usePagination
    );

  return (
    <>
      <table
        {...getTableProps()}
        className="w-full text-sm text-left text-gray-500 dark:text-gray-400 table-auto h-5"
      >
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 top-0 position: sticky">
          {headerGroups.map((headerGroup) => {
            const { key: groupKey, ...groupProps } =
              headerGroup.getHeaderGroupProps();
            return (
              <tr key={groupKey} {...groupProps}>
                {headerGroup.headers.map((column) => {
                  if (column.hideHeader === false) return null;

                  const { key: columnKey, ...columnProps } =
                    column.getHeaderProps();

                  return (
                    <th
                      key={column.id}
                      {...columnProps}
                      className="sticky px-6 py-3"
                    >
                      {column.render('Header')}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);

            const { key: rowKey, ...rowProps } = row.getRowProps(); // remove key from spread

            return (
              <tr
                key={row.id || rowKey} // pass key directly
                {...rowProps}
                className="border-b dark:bg-gray-800 dark:border-gray-700 odd:bg-white even:bg-gray-50 odd:dark:bg-gray-800 even:dark:bg-gray-700"
              >
                {row.cells.map((cell) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps(); // remove key from spread
                  return (
                    <td
                      key={cell.column.id || cellKey}
                      {...cellProps}
                      className="px-6 py-4"
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <td colSpan="10000">
              {loading
                ? 'Loading...'
                : `Showing ${rows.length} of ~${total} results`}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="pagination">
        <Button
          variant="default"
          className="py-px"
          onClick={() => onPageChange(0)}
          disabled={pageIndex === 0}
        >
          {'<<'}
        </Button>
        <Button
          variant="default"
          className="py-px"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={pageIndex === 0}
        >
          {'<'}
        </Button>
        <Button
          variant="default"
          className="py-px"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={pageIndex + 1 >= pageCount}
        >
          {'>'}
        </Button>
        <Button
          variant="default"
          className="py-px"
          onClick={() => onPageChange(pageCount - 1)}
          disabled={pageIndex + 1 >= pageCount}
        >
          {'>>'}
        </Button>

        <span className="ml-2">
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageCount}
          </strong>
        </span>

        <span>
          | Go to page:{' '}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const newPage = e.target.value ? Number(e.target.value) - 1 : 0;
              onPageChange(newPage);
            }}
            style={{ width: '100px' }}
          />
        </span>

        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>

        <span>
          Showing {pageIndex * pageSize + 1} –{' '}
          {Math.min((pageIndex + 1) * pageSize, total)} of {total} results
        </span>
      </div>
    </>
  );
}

function Index({ columns, tableData, loading, pagination, fetchUpdatedData }) {
  const [pageIndex, setPageIndex] = React.useState(pagination?.pageIndex || 0);
  const [pageSize, setPageSize] = React.useState(pagination?.pageSize || 10);

  const handlePageChange = (index) => {
    setPageIndex(index);
    fetchUpdatedData({ pageIndex: index, pageSize });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageIndex(0);
    fetchUpdatedData({ pageIndex: 0, pageSize: size });
  };

  return (
    <Styles>
      <Table
        columns={columns}
        data={tableData}
        loading={loading}
        pageCount={pagination?.totalPages || 0}
        total={pagination?.total || 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </Styles>
  );
}

export default Index;
